/* eslint-disable react/prop-types */
import React from 'react';
import { Alert, Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Button } from 'components/index';
import { Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon, Delete, Warning as WarningIcon } from '@mui/icons-material';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Cell } from 'material-react-table';
import { toast } from 'react-toastify';
import { SectionFieldDTO } from '../magic-report.type';
import { ProjectLineItemDTO } from '../../../project/project.type';
import { formatCurrency } from '../../../../common/utils/common.util';
import { usePrompt } from '../../../../components/AppPrompt';
import { LineItemLabelEnum } from '../../../glossary/glossary.type';

export interface ChangeOrderRow {
  lineItemId: string;
  coNumber: string;
  description: string;
  doWeHaveDoc: 'Yes' | 'No';
  daysExtended: number | null;
  contractor: 'Yes' | 'No';
  architect: 'Yes' | 'No';
  owner: 'Yes' | 'No';
  prevCO: number;
  currCO: number;
  isLastMonth?: boolean;
  /**
   * Which covered draw this row belongs to. All draws' rows share the single `ChangeOrdersRows`
   * field (the backend's reset transform matches that exact field name to carry rows forward), so
   * the draw is recorded per row instead of per field. Absent on rows saved before multi-draw —
   * those are bound to the first covered draw on load.
   */
  drawId?: string;
}

/**
 * A change-order row's primary key within a draw: the DrawLineItem id, which identifies one
 * specific budget line of one specific draw and is stable for that draw across report periods.
 * It is what a carried-over row is matched back to its budget line by.
 */
export const changeOrderRowKey = (li: ProjectLineItemDTO): string => li.id;

/** Also accepts the LineItem id, which rows were briefly keyed on. */
export const rowMatchesLineItem = (row: ChangeOrderRow, li: ProjectLineItemDTO): boolean =>
  row.lineItemId === li.id || (!!li.lineItem?.id && row.lineItemId === li.lineItem.id);

/** Highest CO # already in use, so appended rows continue the sequence instead of renumbering it. */
export const highestCoNumber = (rows: ChangeOrderRow[]): number =>
  rows.reduce((max, r) => {
    const n = parseInt(String(r.coNumber ?? ''), 10);
    return !isNaN(n) && n > max ? n : max;
  }, 0);

const parseRow = (value?: string): ChangeOrderRow | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    // Backward compat: coNumber may be stored as a number from older data
    if (parsed && typeof parsed.coNumber === 'number') {
      parsed.coNumber = String(parsed.coNumber);
    }
    return parsed as ChangeOrderRow;
  } catch {
    return null;
  }
};

const serializeRow = (row: ChangeOrderRow): string => JSON.stringify(row);

const ensureRowDefaults = (row: Partial<ChangeOrderRow>, lineItem?: ProjectLineItemDTO): ChangeOrderRow => {
  return {
    lineItemId: row.lineItemId || '',
    coNumber: row.coNumber != null ? String(row.coNumber) : '1',
    description: row.description || lineItem?.lineItem?.description || '',
    doWeHaveDoc: row.doWeHaveDoc || 'No',
    daysExtended: row.daysExtended ?? null,
    contractor: row.contractor || 'No',
    architect: row.architect || 'No',
    owner: row.owner || 'No',
    prevCO: row.prevCO ?? (lineItem?.changeOrder || 0),
    currCO: row.currCO ?? (row as any).newCO ?? 0,
    isLastMonth: row.isLastMonth ?? false,
    // Preserved so a row never loses which draw it belongs to when normalized.
    drawId: row.drawId,
  };
};

/** Budget movement below this is rounding noise, not a corrected change order. */
const BUDGET_AMOUNT_TOLERANCE = 0.01;

interface ChangeOrdersTableProps {
  rowsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  lineItems: ProjectLineItemDTO[] | undefined;
  onRowsSynced?: (rows: ChangeOrderRow[]) => void;
  onSyncFunctionReady?: (syncFn: () => Promise<void>) => void;
  totalsMatch?: boolean;
  /** Whether the table may fill and re-sync itself from the budget (first open of a period only). */
  autoSync?: boolean;
  /** The saved contract summary no longer matches the budget. */
  summaryOutOfDate?: boolean;
  /** Runs after Get Change Orders, so the section can refresh its summary too. */
  onGet?: () => void;
}

export const ChangeOrdersTable: React.FC<ChangeOrdersTableProps> = ({
  rowsField,
  handleFieldChange,
  lineItems,
  onRowsSynced,
  onSyncFunctionReady,
  totalsMatch = true,
  autoSync = false,
  summaryOutOfDate = false,
  onGet,
}) => {
  const { getConfirmation } = usePrompt();
  const rowsToRenderRef = React.useRef<ChangeOrderRow[]>([]);
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  // Keyed on content, not the array reference: unrelated writes elsewhere in the section give
  // `rowsField.entries` a new reference and used to falsely re-trigger a budget sync.
  const entriesKey = rowsField?.entries?.map(e => e.value).join('||') ?? '';
  const currentRows: ChangeOrderRow[] = React.useMemo(
    () => rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is ChangeOrderRow => r !== null) || [],
    [entriesKey],
  );

  const changeOrderItems = React.useMemo(
    () =>
      (lineItems || []).filter(li => {
        if (li.isDeleted) return false;
        return (li.labels || []).includes(LineItemLabelEnum.ChangeOrder);
      }),
    [lineItems],
  );

  /**
   * Replaces any row whose change-order amount no longer matches the budget.
   *
   * An updated amount makes the change order this period's business again, so the row is rebuilt
   * as a brand new one: signature flags and days extended reset, `isLastMonth` cleared so it stops
   * rendering greyed out, and the whole updated amount in Current. Its CO # and position are kept.
   *
   * Rows whose amount is unchanged are left exactly as they are — that is what keeps last month's
   * carry-overs greyed out and preserves everything the reviewer entered on them. The reset folds
   * Current into Previous without changing a row's total, so an untouched carry-over still matches
   * its budget line and is never rebuilt.
   */
  const rebuildChangedRows = (rows: ChangeOrderRow[]): ChangeOrderRow[] =>
    rows.map(row => {
      const li = changeOrderItems.find(item => rowMatchesLineItem(row, item));
      if (!li) return row;

      const budgetAmount = li.changeOrder || 0;
      const rowAmount = (row.prevCO || 0) + (row.currCO || 0);
      if (Math.abs(budgetAmount - rowAmount) <= BUDGET_AMOUNT_TOLERANCE) return row;

      return ensureRowDefaults(
        {
          lineItemId: changeOrderRowKey(li),
          drawId: row.drawId,
          coNumber: row.coNumber,
          description: li.lineItem?.description || '',
          prevCO: 0,
          currCO: budgetAmount,
        },
        li,
      );
    });

  /**
   * Rows for change-order line items that aren't represented in `existing` yet.
   *
   * Rows already in the table are never rebuilt here — see `rebuildChangedRows` for the one case
   * where an existing row is replaced.
   */
  const buildRowsForNewLineItems = (existing: ChangeOrderRow[]): ChangeOrderRow[] => {
    let nextCoNumber = highestCoNumber(existing);

    return changeOrderItems
      .filter(li => !existing.some(row => rowMatchesLineItem(row, li)))
      .map(li =>
        ensureRowDefaults(
          {
            lineItemId: changeOrderRowKey(li),
            coNumber: String(++nextCoNumber),
            description: li.lineItem?.description || '',
            // A change order the table hasn't seen before is new this period, so its whole amount
            // belongs in Current. It moves to Previous at the next rollover, when the reset folds
            // currCO into prevCO.
            prevCO: 0,
            currCO: li.changeOrder || 0,
          },
          li,
        ),
      );
  };

  /**
   * Adds any change orders from the budget that aren't in the table yet, and replaces any row
   * whose amount has changed. Runs automatically whenever the budget's change-order line items
   * change — manual row creation is disallowed, so this is the only way rows are added.
   */
  // Skip if already syncing, or if the budget hasn't changed since the last sync.
  const isSyncingRef = React.useRef(false);
  const lastSyncSignatureRef = React.useRef<string | null>(null);

  const syncRowsFromBudget = async () => {
    if (isSyncingRef.current) return;
    const signature = changeOrderItems.map(li => `${changeOrderRowKey(li)}:${li.changeOrder ?? 0}`).join('|');
    if (lastSyncSignatureRef.current === signature) return;

    isSyncingRef.current = true;
    try {
      // The ref, not currentRows: it holds rows built for display but not yet persisted, so the
      // first sync on a fresh table saves them instead of appending a duplicate set.
      const existingRows = rebuildChangedRows(rowsToRenderRef.current);
      const addedRows = buildRowsForNewLineItems(existingRows);
      const combinedRows = [...existingRows, ...addedRows];

      const entries =
        combinedRows.length === 0
          ? []
          : combinedRows.map((row, index) => ({
              order: index,
              value: serializeRow(row),
            }));

      handleFieldChange('ChangeOrdersRows', {
        entries,
      });

      rowsToRenderRef.current = combinedRows;
      lastSyncSignatureRef.current = signature;
      onRowsSynced?.(combinedRows);
    } finally {
      isSyncingRef.current = false;
    }
  };

  const handleSaveCell = React.useCallback(
    (cell: MRT_Cell<ChangeOrderRow>, value: any) => {
      // Always use the ref which has the most current data
      const latestRows: ChangeOrderRow[] = [...rowsToRenderRef.current];

      const row = cell.row.original;
      const field = cell.column.id as keyof ChangeOrderRow;

      let updatedValue: any = value;

      // Handle numeric fields
      if (field === 'daysExtended' || field === 'prevCO' || field === 'currCO') {
        updatedValue = field === 'daysExtended' && (value === '' || value === null) ? null : parseFloat(value) || 0;
      }

      const updatedRow = { ...row, [field]: updatedValue } as ChangeOrderRow;
      const updatedRows = latestRows.map(r => (r.lineItemId === row.lineItemId ? updatedRow : r));

      const entries = updatedRows.map((r, index) => ({
        order: index,
        value: serializeRow(r),
      }));

      handleFieldChange('ChangeOrdersRows', { entries });
      // Update the ref immediately so subsequent saves use the latest data
      rowsToRenderRef.current = updatedRows;
      // Trigger comment regeneration when rows change
      onRowsSynced?.(updatedRows);
    },
    [handleFieldChange, onRowsSynced],
  );

  /**
   * Previous/Current CO amounts are derived from the budget, so they are read-only here. The
   * toastId keeps repeated clicks from stacking duplicate toasts.
   */
  const notifyAmountReadOnly = React.useCallback(() => {
    toast.warning('Previous and Current CO amounts cannot be edited here. Edit the amount in the table in the Review Budget step.', {
      toastId: 'change-order-amount-read-only',
    });
  }, []);

  // Toggle helper for Yes/No fields on single click
  const handleToggleYesNo = (cell: MRT_Cell<ChangeOrderRow>) => {
    const value = cell.getValue<'Yes' | 'No'>();
    const newValue = value === 'Yes' ? 'No' : 'Yes';
    handleSaveCell(cell, newValue);
  };

  /**
   * Flips a row between This Month (new CO) and Previous (carried-over), moving the whole amount
   * with it so the row's total — and its match to the budget line — stays intact.
   *
   * A row's prevCO/currCO should never both be nonzero, but if stale data has it, warn before
   * collapsing them into one total, since a row can only carry a single isLastMonth flag.
   */
  const handleTogglePeriod = React.useCallback(
    (row: ChangeOrderRow) => {
      if ((row.prevCO || 0) !== 0 && (row.currCO || 0) !== 0) {
        toast.warning('This row has both Previous and Current CO amounts. Toggling will combine them into one total.', {
          toastId: `change-order-split-merge-${row.lineItemId}`,
        });
      }

      const latestRows = [...rowsToRenderRef.current];
      const nextIsLastMonth = !row.isLastMonth;
      const total = (row.prevCO || 0) + (row.currCO || 0);
      const updatedRow: ChangeOrderRow = {
        ...row,
        isLastMonth: nextIsLastMonth,
        prevCO: nextIsLastMonth ? total : 0,
        currCO: nextIsLastMonth ? 0 : total,
      };
      const updatedRows = latestRows.map(r => (r.lineItemId === row.lineItemId ? updatedRow : r));

      const entries = updatedRows.map((r, index) => ({
        order: index,
        value: serializeRow(r),
      }));

      handleFieldChange('ChangeOrdersRows', { entries });
      rowsToRenderRef.current = updatedRows;
      onRowsSynced?.(updatedRows);
    },
    [handleFieldChange, onRowsSynced],
  );

  const handleDeleteRow = async (rowId: string) => {
    const isConfirmed = await getConfirmation({
      heading: 'Delete Change Order',
      description: 'Are you sure you want to delete this change order row?',
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });

    if (!isConfirmed) return;

    const latestRows: ChangeOrderRow[] =
      rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is ChangeOrderRow => r !== null) || [];

    const updatedRows = latestRows.filter(r => r.lineItemId !== rowId);

    const entries = updatedRows.map((r, index) => ({
      order: index,
      value: serializeRow(r),
    }));

    handleFieldChange('ChangeOrdersRows', { entries });
    onRowsSynced?.(updatedRows);
  };

  const handleBulkDelete = async (selectedRowIds: string[]) => {
    const count = selectedRowIds.length;
    const isConfirmed = await getConfirmation({
      heading: 'Delete Change Orders',
      description: `Are you sure you want to delete ${count} change order row${count > 1 ? 's' : ''}?`,
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });

    if (!isConfirmed) return;

    const latestRows: ChangeOrderRow[] = [...rowsToRenderRef.current];

    const updatedRows = latestRows.filter(r => !selectedRowIds.includes(r.lineItemId));

    const entries = updatedRows.map((r, index) => ({
      order: index,
      value: serializeRow(r),
    }));

    handleFieldChange('ChangeOrdersRows', { entries });
    rowsToRenderRef.current = updatedRows;
    onRowsSynced?.(updatedRows);
    setRowSelection({});
  };

  const rowsToRender = React.useMemo(() => {
    const rows = currentRows.length || !autoSync ? currentRows : buildRowsForNewLineItems([]);
    rowsToRenderRef.current = rows;
    return rows;
  }, [currentRows, lineItems, autoSync]);

  /**
   * Whether the table's rows are out of step with the budget: a change-order line item with no
   * matching row (seeding an empty table counts as this too — every item is "unmatched"), or an
   * existing row whose amount no longer matches its budget line.
   *
   * Checked on every render — including the first, so navigating straight to this step after
   * marking a line item as a change order elsewhere still catches up — rather than only on
   * changes after mount, since a fresh mount can already be behind the budget.
   */
  const needsBudgetSync = React.useMemo(() => {
    const hasUnmatchedItem = changeOrderItems.some(li => !currentRows.some(row => rowMatchesLineItem(row, li)));
    if (hasUnmatchedItem) return true;
    return currentRows.some(row => {
      const li = changeOrderItems.find(item => rowMatchesLineItem(row, item));
      if (!li) return false;
      const budgetAmount = li.changeOrder || 0;
      const rowAmount = (row.prevCO || 0) + (row.currCO || 0);
      return Math.abs(budgetAmount - rowAmount) > BUDGET_AMOUNT_TOLERANCE;
    });
  }, [changeOrderItems, currentRows]);

  // Re-syncs from the budget on its own only during the first open of a period; after that the
  // table only changes through Get Change Orders.
  React.useEffect(() => {
    if (needsBudgetSync && autoSync) {
      syncRowsFromBudget();
    }
  }, [needsBudgetSync, autoSync]);

  // Expose sync function to parent
  React.useEffect(() => {
    if (onSyncFunctionReady) {
      onSyncFunctionReady(() => syncRowsFromBudget());
    }
  }, [onSyncFunctionReady]);

  const duplicateCoNumbers = React.useMemo(() => {
    const counts = new Map<string, number>();
    rowsToRender.forEach(r => {
      const n = String(r.coNumber ?? '').trim();
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const dupes = new Set<string>();
    counts.forEach((c, n) => {
      if (c > 1) dupes.add(n);
    });
    return dupes;
  }, [rowsToRender]);

  // Calculate totals
  const totalsRow = React.useMemo(
    () => ({
      daysExtendedTotal: rowsToRender.reduce((sum, row) => sum + (row.daysExtended || 0), 0),
      COTotal: rowsToRender.reduce((sum, row) => sum + (row.prevCO ?? (row as any).newCO ?? 0) + (row.currCO ?? 0), 0),
    }),
    [rowsToRender],
  );

  const rightAlignedProps = {
    muiTableBodyCellProps: {
      align: 'right' as const,
      sx: {
        fontSize: '0.875rem',
        border: 'none',
      },
    },
    muiTableHeadCellProps: {
      align: 'right' as const,
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        position: 'sticky',
        top: 0,
        color: '#ffffff',
        padding: '8px 9px',
        border: 'none',
        justifyContent: 'center',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '& .Mui-TableHeadCell-Content-Wrapper': {
          justifyContent: 'flex-end',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      }),
    },
    muiTableFooterCellProps: {
      align: 'right' as const,
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        fontWeight: 'normal',
        fontSize: '0.9rem',
        padding: '8px 9px',
        border: 'none',
      }),
    },
  };

  /** Read-only amount cell: clicking or double-clicking explains where the value is edited instead. */
  const readOnlyAmountCellProps = {
    align: rightAlignedProps.muiTableBodyCellProps.align,
    onClick: notifyAmountReadOnly,
    onDoubleClick: notifyAmountReadOnly,
    sx: {
      ...rightAlignedProps.muiTableBodyCellProps.sx,
      cursor: 'not-allowed',
    },
  };

  // Derive selectedRowIds before columns definition so it's available in the Header function
  const selectedRowIds = React.useMemo(() => Object.keys(rowSelection).filter(key => rowSelection[key]), [rowSelection]);

  const columns = React.useMemo<MRT_ColumnDef<ChangeOrderRow>[]>(
    () => [
      {
        accessorKey: 'coNumber',
        header: 'CO #',
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          const isDuplicate = duplicateCoNumbers.has(value.trim());
          if (!isDuplicate) return <>{value}</>;
          return (
            <Tooltip title='Duplicate CO #' arrow>
              <Box
                sx={{
                  color: 'warning.dark',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  justifyContent: 'flex-end',
                }}
              >
                <WarningIcon sx={{ fontSize: 14 }} />
                {value}
              </Box>
            </Tooltip>
          );
        },
        muiEditTextFieldProps: ({ cell }) => ({
          type: 'text',
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        ...rightAlignedProps,
        muiTableHeadCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            position: 'sticky',
            top: 0,
            color: '#ffffff',
            padding: '8px 9px',
            border: 'none',
            justifyContent: 'center',
            fontSize: '0.9rem',
            fontWeight: 'normal',
            '&[data-pinned="true"]:not([role="columnheader"])::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]:not([role="columnheader"])': {
              zIndex: 1,
            },
          }),
        },
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
            border: 'none',
            fontSize: '0.875rem',
            '&[data-pinned="true"]::before': {
              backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
            },
          },
        }),
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            fontWeight: 'normal',
            fontSize: '0.9rem',
            padding: '8px 9px',
            border: 'none',
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
          }),
        },
        Footer: () => '',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 200,
        enableEditing: true,
        muiEditTextFieldProps: ({ cell }) => ({
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        Footer: () => '',
        muiTableHeadCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            position: 'sticky',
            top: 0,
            color: '#ffffff',
            padding: '8px 9px',
            border: 'none',
            justifyContent: 'center',
            fontSize: '0.9rem',
            fontWeight: 'normal',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            '&[data-pinned="true"]:not([role="columnheader"])::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]:not([role="columnheader"])': {
              zIndex: 1,
            },
          }),
        },
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
            border: 'none',
            fontSize: '0.875rem',
            '&[data-pinned="true"]::before': {
              backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
            },
          },
        }),
        muiTableFooterCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            fontWeight: 'normal',
            fontSize: '0.9rem',
            padding: '8px 9px',
            border: 'none',
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
          }),
        },
      },
      {
        accessorKey: 'doWeHaveDoc',
        header: 'Do we have doc?',
        size: 120,
        enableEditing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<'Yes' | 'No'>();
          return (
            <Typography
              variant='body2'
              sx={{
                color: value === 'Yes' ? 'success.main' : 'error.main',
                fontWeight: 'medium',
              }}
            >
              {value}
            </Typography>
          );
        },
        Footer: () => '',
        muiTableBodyCellProps: ({ cell }) => ({
          align: rightAlignedProps.muiTableBodyCellProps.align,
          onClick: e => {
            e.stopPropagation();
            e.preventDefault();
            handleToggleYesNo(cell as MRT_Cell<ChangeOrderRow>);
          },
          sx: {
            ...rightAlignedProps.muiTableBodyCellProps.sx,
            cursor: 'pointer',
          },
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        muiTableFooterCellProps: rightAlignedProps.muiTableFooterCellProps,
      },
      {
        accessorKey: 'daysExtended',
        header: 'Days Extended',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          return value !== null ? value.toString() : '';
        },
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          type: 'number',
          onBlur: event => {
            handleSaveCell(cell, event.target.value === '' ? null : event.target.value);
          },
        }),
        ...rightAlignedProps,
        Footer: () => totalsRow.daysExtendedTotal.toString(),
      },
      {
        accessorKey: 'contractor',
        header: 'Contractor',
        size: 100,
        enableEditing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<'Yes' | 'No'>();
          const noDoc = cell.row.original.doWeHaveDoc === 'No';
          if (noDoc)
            return (
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                —
              </Typography>
            );
          return (
            <Typography
              variant='body2'
              sx={{
                color: value === 'Yes' ? 'success.main' : 'error.main',
                fontWeight: 'medium',
              }}
            >
              {value}
            </Typography>
          );
        },
        Footer: () => 'CO Total',
        muiTableBodyCellProps: ({ cell }) => {
          const noDoc = cell.row.original.doWeHaveDoc === 'No';
          return {
            align: rightAlignedProps.muiTableBodyCellProps.align,
            onClick: noDoc
              ? undefined
              : e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleToggleYesNo(cell as MRT_Cell<ChangeOrderRow>);
                },
            sx: {
              ...rightAlignedProps.muiTableBodyCellProps.sx,
              cursor: noDoc ? 'default' : 'pointer',
            },
          };
        },
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        muiTableFooterCellProps: rightAlignedProps.muiTableFooterCellProps,
      },
      {
        accessorKey: 'architect',
        header: 'Architect',
        size: 100,
        enableEditing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<'Yes' | 'No'>();
          const noDoc = cell.row.original.doWeHaveDoc === 'No';
          if (noDoc)
            return (
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                —
              </Typography>
            );
          return (
            <Typography
              variant='body2'
              sx={{
                color: value === 'Yes' ? 'success.main' : 'error.main',
                fontWeight: 'medium',
              }}
            >
              {value}
            </Typography>
          );
        },
        Footer: () => '',
        muiTableBodyCellProps: ({ cell }) => {
          const noDoc = cell.row.original.doWeHaveDoc === 'No';
          return {
            align: rightAlignedProps.muiTableBodyCellProps.align,
            onClick: noDoc
              ? undefined
              : e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleToggleYesNo(cell as MRT_Cell<ChangeOrderRow>);
                },
            sx: {
              ...rightAlignedProps.muiTableBodyCellProps.sx,
              cursor: noDoc ? 'default' : 'pointer',
            },
          };
        },
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        muiTableFooterCellProps: rightAlignedProps.muiTableFooterCellProps,
      },
      {
        accessorKey: 'owner',
        header: 'Owner',
        size: 100,
        enableEditing: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<'Yes' | 'No'>();
          const noDoc = cell.row.original.doWeHaveDoc === 'No';
          if (noDoc)
            return (
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                —
              </Typography>
            );
          return (
            <Typography
              variant='body2'
              sx={{
                color: value === 'Yes' ? 'success.main' : 'error.main',
                fontWeight: 'medium',
              }}
            >
              {value}
            </Typography>
          );
        },
        Footer: () => '',
        muiTableBodyCellProps: ({ cell }) => {
          const noDoc = cell.row.original.doWeHaveDoc === 'No';
          return {
            align: rightAlignedProps.muiTableBodyCellProps.align,
            onClick: noDoc
              ? undefined
              : e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleToggleYesNo(cell as MRT_Cell<ChangeOrderRow>);
                },
            sx: {
              ...rightAlignedProps.muiTableBodyCellProps.sx,
              cursor: noDoc ? 'default' : 'pointer',
            },
          };
        },
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        muiTableFooterCellProps: rightAlignedProps.muiTableFooterCellProps,
      },
      {
        id: 'period',
        header: 'This Month',
        size: 100,
        enableEditing: false,
        Cell: ({ row }) => {
          const value: 'Yes' | 'No' = row.original.isLastMonth ? 'No' : 'Yes';
          return (
            <Typography
              variant='body2'
              sx={{
                color: value === 'Yes' ? 'success.main' : 'error.main',
                fontWeight: 'medium',
              }}
            >
              {value}
            </Typography>
          );
        },
        Footer: () => '',
        muiTableBodyCellProps: ({ row }) => ({
          align: rightAlignedProps.muiTableBodyCellProps.align,
          onClick: e => {
            e.stopPropagation();
            e.preventDefault();
            handleTogglePeriod(row.original);
          },
          sx: {
            ...rightAlignedProps.muiTableBodyCellProps.sx,
            cursor: 'pointer',
          },
        }),
        muiTableHeadCellProps: rightAlignedProps.muiTableHeadCellProps,
        muiTableFooterCellProps: rightAlignedProps.muiTableFooterCellProps,
      },
      {
        accessorKey: 'prevCO',
        header: 'Previous CO',
        size: 120,
        // Derived from the budget — edited in Review Budget, not here.
        enableEditing: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        ...rightAlignedProps,
        muiTableBodyCellProps: readOnlyAmountCellProps,
      },
      {
        accessorKey: 'currCO',
        header: 'Current CO',
        size: 120,
        // Derived from the budget — edited in Review Budget, not here.
        enableEditing: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        ...rightAlignedProps,
        muiTableBodyCellProps: readOnlyAmountCellProps,
        Footer: () => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              padding: '8px 12px',
              backgroundColor: theme => theme.palette.primary.dark,
            }}
          >
            {!totalsMatch && (
              <Tooltip title='Change order amount mismatch' arrow>
                <WarningIcon sx={{ fontSize: 18, color: '#ffffff' }} />
              </Tooltip>
            )}
            <span>{formatCurrency(totalsRow.COTotal.toString(), true)}</span>
          </Box>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableEditing: false,
        enableResizing: false,
        Header: () => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              px: 0,
            }}
          >
            <span>
              <IconButton
                size='small'
                color='error'
                disabled={selectedRowIds.length === 0}
                onClick={async () => {
                  if (selectedRowIds.length === 0) return;
                  await handleBulkDelete(selectedRowIds);
                }}
                sx={{ p: 0.5, m: 0, alignSelf: 'center' }}
              >
                <Delete fontSize='small' />
              </IconButton>
            </span>
          </Box>
        ),
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <IconButton
              size='small'
              onClick={() => handleDeleteRow(row.original.lineItemId)}
              sx={{
                color: '#9e9e9e',
                '&:hover': { color: 'error.main' },
              }}
            >
              <Delete fontSize='small' />
            </IconButton>
          </Box>
        ),
        Footer: () => '',
        muiTableHeadCellProps: {
          align: 'right' as const,
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            position: 'sticky',
            top: 0,
            color: '#ffffff',
            padding: '8px 9px',
            border: 'none',
            justifyContent: 'flex-end',
            '& .Mui-TableHeadCell-Content-Wrapper': {
              justifyContent: 'flex-end',
            },
          }),
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
          sx: {
            border: 'none',
            fontSize: '0.875rem',
            display: 'flex',
            justifyContent: 'flex-end',
          },
        },
        muiTableFooterCellProps: {
          align: 'right' as const,
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            padding: '8px 9px',
            border: 'none',
            justifyContent: 'flex-end',
          }),
        },
      },
    ],
    [totalsRow, handleSaveCell, handleTogglePeriod, selectedRowIds, handleBulkDelete, duplicateCoNumbers],
  );

  const table = useMaterialReactTable({
    columns,
    data: rowsToRender,
    initialState: {
      density: 'compact',
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-select', 'coNumber', 'description'] },
    },
    layoutMode: 'grid',
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowSelection: true,
    enableSorting: false,
    enableStickyFooter: true,
    editDisplayMode: 'cell',
    enableEditing: true,
    getRowId: row => row.lineItemId,
    muiTableProps: {
      sx: {
        marginTop: '0px',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableContainerProps: {
      sx: {
        padding: '0px',
        height: '100%',
        maxHeight: '600px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        overflow: 'auto',
        '& .css-1jh1yab-MuiTableCell-root[data-pinned="true"]': {
          justifyContent: 'center !important',
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '8px 9px',
        border: 'none',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '& .MuiBox-root': {
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
      }),
    },
    muiTableFooterCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        fontWeight: 'normal',
        fontSize: '0.9rem',
        padding: '8px 9px',
        border: 'none',
        '&[data-pinned="true"]::before': {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]': {
          zIndex: 1,
        },
      }),
    },
    muiTableBodyRowProps: ({ row }) => {
      // If isLastMonth is true, use gray background, otherwise use alternating colors
      const backgroundColor = row.original.isLastMonth ? '#e0e0e0' : row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';
      return {
        sx: {
          backgroundColor: backgroundColor + ' !important',
          border: 'none',
          '& td[data-pinned="true"]::before': {
            backgroundColor: backgroundColor,
          },
        },
      };
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        fontSize: '0.875rem',
      },
    },
    state: {
      isLoading: false,
      showProgressBars: false,
      showSkeletons: false,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
  });

  const handleGetChangeOrders = async () => {
    lastSyncSignatureRef.current = null;
    await syncRowsFromBudget();
    onGet?.();
  };

  const outOfDateAlert =
    !autoSync && (needsBudgetSync || summaryOutOfDate) ? (
      <Alert severity='warning' sx={{ mb: 1, fontSize: '0.875rem' }}>
        Change Orders are not synced with the current budget. Click &quot;Get Change Orders&quot; to refresh the data.
      </Alert>
    ) : null;

  if (!rowsToRender.length) {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        {outOfDateAlert}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            No line items marked as change orders were found in the Review Budget step.
          </Typography>
          <Button variant='outlined' size='small' onClick={handleGetChangeOrders}>
            Get Change Orders
          </Button>
        </Box>
      </Box>
    );
  }

  const addToActionItems = rowsField?.addToActionItems || false;

  const handleBookmarkToggle = () => {
    handleFieldChange('ChangeOrdersRows', { addToActionItems: !addToActionItems });
  };

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {outOfDateAlert}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>
        <Typography variant='body2' color='text.secondary'>
          CO Description:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {selectedRowIds.length > 0 && (
            <Button
              variant='outlined'
              size='small'
              color='error'
              onClick={async () => {
                await handleBulkDelete(selectedRowIds);
              }}
            >
              Delete Selected ({selectedRowIds.length})
            </Button>
          )}
          <Button variant='outlined' size='small' onClick={handleGetChangeOrders}>
            Get Change Orders
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, minWidth: 0, overflow: 'auto' }}>
          <MaterialReactTable table={table} />
        </Box>
        <IconButton size='small' onClick={handleBookmarkToggle} sx={{ mt: 0.5, flexShrink: 0 }}>
          {addToActionItems ? (
            <BookmarkIcon color='primary' sx={{ fontSize: 20 }} />
          ) : (
            <BookmarkBorderIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};
